/**
 * What the overview screen derives from the listings it already fetched.
 *
 * Pure functions, so the rules that decide "this is on the air" and "nobody can reach this" are
 * testable without rendering — they are the part of the screen most likely to be wrong and least
 * likely to look wrong.
 *
 * @module Pages/Dashboard
 */

import type { Tenant } from '@/Pages/BUs/BUManager/Components/BUPlatformList/BUPlatformList.types';
import type { Tour } from '@/Pages/BUs/Tours/Types/Tours.types';
import type { WhatsNew } from '@/Pages/BUs/WhatsNew/Types/WhatsNew.types';
import { WhatsNewApprovalStatus } from '@/Pages/BUs/WhatsNew/Types/WhatsNew.types';

/**
 * Whether the BFF would deliver this announcement to a user of the BU right now.
 *
 * Mirrors the delivery query in `platform-user/whats-new`: approved, active, its platform version
 * **exactly** equal to the BU's current one, and published. The exact match is the whole point —
 * an announcement written for 2.31 stops being served the moment the BU moves to 2.32, silently
 * and permanently, which is the incident that made this screen worth building.
 *
 * Compared by version string rather than id because that is what both listings carry. Two
 * versions with the same number cannot exist for the same BU (the unique key is
 * `[tenantId, version]`), so the comparison is exact in practice.
 */
export function isLiveForTenant(item: WhatsNew, tenant: Pick<Tenant, 'slug' | 'version'>): boolean {
  if (item.tenantSlug !== tenant.slug) return false;
  if (item.approvalStatus !== WhatsNewApprovalStatus.APPROVED) return false;
  if (!item.isActive) return false;
  if (item.platformVersion?.version !== tenant.version) return false;

  // A future publishedAt is scheduled, not live. A missing one means "as soon as approved".
  if (item.publishedAt && new Date(item.publishedAt).getTime() > Date.now()) return false;

  return true;
}

/** What a BU is delivering right now. */
export interface LiveContent {
  /** The announcement being served, if any. */
  announcement?: WhatsNew;
  /** Announcements approved for a version this BU has moved past — nobody will ever see them. */
  unreachable: WhatsNew[];
  activeTours: number;
}

export function getLiveContent(
  tenant: Pick<Tenant, 'slug' | 'version'>,
  announcements: WhatsNew[],
  tours: Tour[],
): LiveContent {
  const mine = announcements.filter(item => item.tenantSlug === tenant.slug);

  return {
    announcement: mine.find(item => isLiveForTenant(item, tenant)),
    /*
      Approved, so somebody meant to publish it, but pinned to a version this BU no longer runs.
      Not "scheduled" and not "old": unreachable, and nothing in the product says so.
    */
    unreachable: mine.filter(
      item =>
        item.approvalStatus === WhatsNewApprovalStatus.APPROVED &&
        item.isActive &&
        item.platformVersion?.version !== tenant.version,
    ),
    activeTours: tours.filter(tour => tour.tenantSlug === tenant.slug && tour.isActive).length,
  };
}

/** Content that is live and has never been opened by anyone. */
export interface UnseenContent {
  announcements: WhatsNew[];
  tours: Tour[];
}

/**
 * Live content nobody has opened.
 *
 * `viewerCount` is optional — an older BFF omits it — and an absent count is **not** zero. Reading
 * it as zero would report every announcement as unseen against a deployment that simply does not
 * send the field.
 */
export function getUnseenContent(
  tenants: Pick<Tenant, 'slug' | 'version'>[],
  announcements: WhatsNew[],
  tours: Tour[],
): UnseenContent {
  const live = tenants.flatMap(tenant =>
    announcements.filter(item => isLiveForTenant(item, tenant)),
  );

  return {
    announcements: live.filter(item => item.viewerCount === 0),
    tours: tours.filter(tour => tour.isActive && tour.viewerCount === 0),
  };
}

/** Announcements that stopped moving through the workflow. */
export interface StalledContent {
  drafts: WhatsNew[];
  rejected: WhatsNew[];
  /** The rejected one that has been sitting the longest, if any. */
  oldestRejected?: WhatsNew;
}

/**
 * Drafts and rejections.
 *
 * The approval banner covers `PENDING_APPROVAL` only, so a rejected announcement disappears from
 * the product entirely: nobody returns to it because nothing remembers it exists.
 */
export function getStalledContent(announcements: WhatsNew[]): StalledContent {
  const drafts = announcements.filter(item => item.approvalStatus === WhatsNewApprovalStatus.DRAFT);
  const rejected = announcements.filter(
    item => item.approvalStatus === WhatsNewApprovalStatus.REJECTED,
  );

  const oldestRejected = [...rejected].sort(
    (a, b) =>
      new Date(a.rejectedAt ?? a.updatedAt).getTime() -
      new Date(b.rejectedAt ?? b.updatedAt).getTime(),
  )[0];

  return { drafts, rejected, oldestRejected };
}

/**
 * Reading time for the screen.
 *
 * The BFF reports milliseconds; nobody reads "6000 ms". Under a minute reads as seconds, above it
 * as `1m 14s`, and `null` (nobody reported one) stays a dash rather than becoming a zero.
 */
export function formatReadingTime(durationMs: number | null | undefined): string {
  if (durationMs === null || durationMs === undefined) return '—';

  const totalSeconds = Math.round(durationMs / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds === 0 ? `${minutes}min` : `${minutes}min ${seconds}s`;
}
